import React, { useState, useEffect } from "react";
import styles from "./TextToPay.module.css";
import FireboardUtils from "../../../../util/fireboard-util";
import TextInput from '../../../../components/TextInput';
import PopupHeader from "../PopupHeader";
import { CountryPicker, getPickerData } from "../../../../components/CountryPicker";
import strings from '../../../../globalization';

const US_COUNTRY_CODE = 'us';
const UK_COUNTRY_CODE = 'gb';

export default function TextToPay(props) {
  const { invokeTextToPay, selectedItem, fromFooter = false, onClose, currency = '$', iso2 } = props;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [guestName, setGuestName] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [subTotal, setSubTotal] = useState('');
  const [tax, setTax] = useState('');
  const [selectedCountry, setSelectedCountry] = useState({});
  const [isPreAuth, setIsPreAuth] = useState(false);

  useEffect(() => {
    setDefaultCountry();
  }, []);

  const setDefaultCountry = () => {
    const countriesData = getPickerData();
    const countryCode = currency === '£' ? UK_COUNTRY_CODE : US_COUNTRY_CODE;
    const defaultCountry = countriesData.find(country => country.iso2 === countryCode);
    setSelectedCountry(defaultCountry);
  }

  const onCountryChange = country => {
    const formattedPhoneNumber = FireboardUtils.inputFormattedPhnNum(phoneNumber, country.iso2);
    setSelectedCountry(country);
    setPhoneNumber(formattedPhoneNumber);
  };

  useEffect(() => {
    if (Object.keys(selectedItem).length && !fromFooter) {
      const { tickets, unpaidAmount, selectedCheckDetails } = selectedItem || {};
      const {
        checkNumber: updatedCheckNumber,
        phoneNumber: updatedPhoneNumber,
        pickupDate: updatedPickupDate,
        taxAmount: updatedTaxAmount,
        guestName: updatedGuestName,
        subTotalAmount: updatedSubtotalAmount,
        checkInNote: updatedCheckInNote,
      } = selectedCheckDetails || {};
      const newCheckNumber = updatedCheckNumber || checkNumber;
      const newGuestName = updatedGuestName || guestName || updatedCheckInNote;
      const newPhoneNum = updatedPhoneNumber || phoneNumber;
      const newPickupDate = updatedPickupDate || pickupTime;
      const newSubtotalAmount = updatedSubtotalAmount || subTotal;
      const newTaxAmount = updatedTaxAmount || tax;

      setPhoneNumber(newPhoneNum ? FireboardUtils.formattedPhoneNumber(newPhoneNum, iso2) : '');
      setGuestName(newGuestName ? newGuestName : '');
      setPickupTime(newPickupDate ? FireboardUtils.getTimeForPickup(newPickupDate) : '');
      setCheckNumber(newCheckNumber ? newCheckNumber : '');
      setSubTotal(newSubtotalAmount ? newSubtotalAmount : '');
      setTax(newTaxAmount ? newTaxAmount : '');

      //pre auth check
      const preauthTicket = tickets && tickets.find(ticket => ticket.ticketStatusId === 0 && ticket.preAuth && unpaidAmount > 0);
      setIsPreAuth(preauthTicket ? true : false);
    }

    return function cleanup() {
      reset();
    };
  }, [fromFooter, iso2, selectedItem]);

  const reset = () => {
    setPhoneNumber("");
    setGuestName("");
    setPickupTime("");
    setCheckNumber("");
    setSubTotal("");
    setTax("");
    setDefaultCountry();
  };

  const request = () => {
    let payload = {
      phoneNumber: phoneNumber,
      checkNumber: checkNumber,
      subTotal: subTotal,
      tax: tax,
      selectedCountry: selectedCountry,
    };
    if (isPreAuth) {
      payload['preAuth'] = true;
    } else {
      payload['guestName'] = guestName;
      payload['pickupTime'] = pickupTime;
      payload['preAuth'] = false;
    }

    invokeTextToPay && invokeTextToPay(payload);
  };

  const changeTime = (time) => {
    const updatedTime = `${time}:00`;
    setPickupTime(updatedTime);
  };

  const disableCheckNumber = () => {
    return !!(props.selectedItem?.selectedCheckDetails?.checkNumber);
  }

  const disableSubTotal = () => {
    return !!(props.selectedItem?.selectedCheckDetails?.subTotalAmount);
  }

  const disableTax = () => {
    return !!(props.selectedItem?.selectedCheckDetails?.taxAmount);
  }

  const isDisabled = () => {
    let isDisabled = true;
    if (isPreAuth) {
      if (phoneNumber.length > 0 &&
        checkNumber.length > 0 &&
        String(subTotal).length > 0 &&
        String(tax).length > 0)
        isDisabled = false;
    } else {
      if (phoneNumber.length > 0 &&
        guestName.length > 0 &&
        checkNumber.length > 0 &&
        String(subTotal).length > 0 &&
        String(tax).length > 0 &&
        pickupTime.length >= 5)
        isDisabled = false;
    }
    return isDisabled;
  };

  const amtPattern = /^(?:\d*\.\d{1,2}|\d+)$/;

  const adjustSubTotal = amt => {
    if (amt === "") setSubTotal("");
    if (amt.match(amtPattern)) {
      setSubTotal(amt);
    }
  }

  const adjustTax = amt => {
    if (amt === "") setTax("");
    if (amt.match(amtPattern)) {
      setTax(amt);
    }
  }

  return (
    <div className={styles.container}>
      <PopupHeader title={strings.textToPayGenHeading} onClose={onClose} containerStyle={styles.heading} />
      <div className={styles.container} >
        <div>
          <label className={styles.label}>{strings.countryCode}</label>
          <CountryPicker selectedCountry={selectedCountry} onCountryChange={onCountryChange} />
        </div>
        <TextInput type="tel" placeholder={strings.placeholderPhone} value={FireboardUtils.inputFormattedPhnNum(phoneNumber, selectedCountry.iso2)} onChange={val => setPhoneNumber(val)} inputStyle={styles.textInput} />
        {!isPreAuth && (
          <>
            <TextInput type="text" placeholder={strings.placeholderGuest} value={guestName} onChange={val => setGuestName(val)} inputStyle={styles.textInput} />
            <TextInput type={'time'} placeholder={strings.placeholderPickupTime} value={pickupTime} onChange={val => changeTime(val)} inputStyle={styles.textInput} />
          </>
        )}
        <TextInput type="tel" placeholder={strings.placeholderCheck} disabled={disableCheckNumber()} value={checkNumber} onChange={val => setCheckNumber(val)} inputStyle={styles.textInput} />
        <TextInput type="number" placeholder={strings.placeholderSubTotal} disabled={disableSubTotal()} pattern="^\d*(\.\d{0,2})?$" value={subTotal} onChange={val => adjustSubTotal(val)} inputStyle={styles.textInput} />
        <TextInput type="number" placeholder={strings.placeholderTax} disabled={disableTax()} value={tax} onChange={val => adjustTax(val)} inputStyle={styles.textInput} />
      </div>
      <div className={styles.btnWrapper}>
        <button className={styles.btns} onClick={reset}>{strings.resetBtn}</button>
        <button className={styles.btns} onClick={request} disabled={isDisabled()} style={{ opacity: isDisabled() ? 0.4 : 1 }}>{strings.requestBtn}</button>
      </div>
    </div>
  );
};
